import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Alert from '../../features/error/Alert'
import JNJFooter from '../../features/jnj-footer/JNJFooter'
import { Button } from '../../features/section-components/Button'
import { SectionHeader } from '../../features/section-components/SectionHeader'
import { selectUserCallStatus, fetchUser } from '../../features/user/userSlice'
import {
  selectUserPhone,
  fetchPhoneNumber,
} from '../EditPhoneNumber/userPhoneSlice'
import MarginWrapper from '../../features/wrappers/MarginWrapper'
import { LoadingIcon } from '../../features/section-components/LoadingIcon'
import { TextboxDesktop } from '../../features/section-components/Textbox'
import { gatewayRequestAxios } from '../../api/interceptor'
import { GATEWAY_API_AUTH_PHONE } from '../../util/config'
import styles from './VerifyPhone.module.scss'
import isMobileView from '../../util/MobileView'
import { convertPhoneToE164WithCountryCode } from '../../util/phoneUtils'
import '../../util/i18n'

export function VerifyPhone() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isMobile = isMobileView()
  const userCallStatus = useSelector(selectUserCallStatus)
  const userPhoneArray = useSelector(selectUserPhone)
  const [authenticationCode, setAuthenticationCode] = useState('')
  const [showAlert, setShowAlert] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [authButtonisLoading, setAuthButtonisLoading] = useState(false)
  const hasFetchedAUser = sessionStorage.getItem('authInitialized')

  const removeSessionStorage = () => {
    sessionStorage.removeItem('authInitialized')
  }

  useEffect(() => {
    if (userCallStatus === 'idle') {
      dispatch(fetchUser())
    }
  }, [userCallStatus, dispatch])

  useEffect(() => {
    if (hasFetchedAUser) {
      if (userPhoneArray?.phoneField) {
        setShowAlert(true)
        setIsLoading(false)
      }
    } else {
      dispatch(fetchPhoneNumber(' ')).then(() => {
        sessionStorage.setItem('authInitialized', true)
        setShowAlert(true)
        setIsLoading(false)
      })
      window.addEventListener('beforeunload', removeSessionStorage)
    }
  }, [userPhoneArray])

  const handleChange = (value) => {
    setAuthenticationCode(value)
  }

  const handleSubmit = () => {
    setAuthButtonisLoading(true)
    setIsLoading(true)
    const formattedPhoneNumber = convertPhoneToE164WithCountryCode(
      userPhoneArray?.phoneField,
      userPhoneArray?.currentCountryPhoneCode
    )
    const reqJson = {
      passCode: authenticationCode,
      phoneNumber: formattedPhoneNumber,
    }
    gatewayRequestAxios
      .patch(GATEWAY_API_AUTH_PHONE, reqJson)
      .then(() => {
        setAuthButtonisLoading(false)
        setHasError(false)
        setShowAlert(true)
        setIsLoading(false)

        return dispatch(fetchPhoneNumber(' '))
      })
      .then(() => {
        navigate('/account')
      })
      .catch(() => {
        setAuthButtonisLoading(false)
        setHasError(true)
        setShowAlert(true)
        setIsLoading(false)
      })
  }

  const isPageLoading = isLoading || authButtonisLoading
    || userCallStatus === 'loading' || userCallStatus === 'failed' || userCallStatus === 'idle'
    || !userPhoneArray?.phoneField

  return (
    <MarginWrapper>
      <LoadingIcon isLoading={isPageLoading}>
        {!isMobile && <div className={styles['verify-phone-post-header']} />}
        <div
          className={
            isMobile
              ? styles['verify-phone-mobile']
              : styles['verify-phone-container']
          }
        >
          <div className={styles['back-section']}>
            <Link
              to="/edit-phone"
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
              color="black"
            >
              <ArrowBackIcon className={styles['back-icon']} sx="medium" />
              <div className={styles['back-text']}>{t('button-back')}</div>
            </Link>
          </div>

          <div className={styles['form-text-container']}>
            <SectionHeader text={t('label-phone-num')} size="medium" />
            <span
              className={
                isMobile
                  ? styles['form-secondary-text-mobile']
                  : styles['form-secondary-text']
              }
            >
              {t('support-text-verify-phone')}
            </span>
          </div>

          {(showAlert && userPhoneArray?.phoneNumber !== '') && (
            <div
              className={
                isMobile
                  ? styles['alert-container-mobile']
                  : styles['alert-container']
              }
            >
              <Alert
                message={
                  hasError ? (
                    t('error-auth')
                  ) : (
                    <>
                      {t('success-auth-phone-start')}{' '}
                      <strong>
                        {userPhoneArray?.currentCountryPhoneCode}
                        {userPhoneArray?.phoneField}
                      </strong>
                      {t('success-auth-phone-end')}
                    </>
                  )
                }
                onClose={() => { }}
                alertStyle={hasError ? 'error-alert' : 'success-no-underline'}
                headerText={hasError ? 'Failed' : 'Success'}
              />
            </div>
          )}

          <div className={styles['form-field']}>
            <span className={styles['form-label']}>
              {t('label-verify-phone-auth-input')}
            </span>
            <div>
              <TextboxDesktop
                borderRadius="4px"
                maxWidth="193px !important"
                minWidth="0 !important"
                isNumeric
                value={authenticationCode}
                onChange={(e) => handleChange(e.target.value)}
                required
              />
            </div>
          </div>
          <Button
            text={authButtonisLoading ? '' : t('button-auth')}
            type="button"
            onClick={handleSubmit}
            buttonStyle="cta-red-oval"
            disabled={authButtonisLoading}
            icon={authButtonisLoading ? <LoadingIcon isLoading size="small" color="white" /> : null}
          />

          <div className={styles['verify-phone-links-container']}>
            {/* <Link to="/" className={styles['verify-phone-link']}>
              {t('link-verify-phone-auth-different-method')}
            </Link> */}
            <Link to="/" className={styles['verify-phone-link']}>
              {t('link-verify-phone-go-back')}
            </Link>
          </div>
        </div>

        <div className={styles['JNJ-footer']}>
          <JNJFooter />
        </div>
      </LoadingIcon>
    </MarginWrapper>
  )
}

export default VerifyPhone
